import { useMemo, useEffect, useRef } from 'react'
import { useAppStore } from '@/store'
import type { ProduccionCabecera, PlanificacionSemanalRow, RoturaFilter, PendingStatusFilter, SortKey } from '@/types'
import { parseDate } from '@/utils/planningFormatters'
import { matchesItemType, type ItemType } from '@/utils/itemType'
import {
  catalogoDe, predicadoVariasGrafias, predicadoTextoAgregado,
} from '@/utils/filtroTexto'
import { asEstadoMaterial, cuentaComoRotura, peorEstado } from '@/utils/estadoMaterial'
import { catalogoArticulos } from '@/utils/filtroArticulo'
import type { ComponentStatus } from '@/types/productionSequence'
import { opsAutoDesplegadas } from '@/utils/planningAutoDespliegue'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { useAllowedEmpresas } from '@/hooks/useAllowedEmpresas'
import { useIsEmpresaAll } from '@/hooks/useIsEmpresaAll'
import { useIsAvpOnly } from '@/hooks/useIsAvpOnly'

/** Tope de subtablas que se abren solas al buscar. Ver `opsAutoDesplegadas`. */
const MAX_AUTO_DESPLIEGUE = 10

export function usePlanningFilters(
  producciones: ProduccionCabecera[],
  planRows: PlanificacionSemanalRow[],
  planByProd: Map<string, PlanificacionSemanalRow[]>,
  prodsConRotura: Set<string>,
) {
  // Slice de filtros COMPARTIDO entre vista semanal y mensual.
  const { planFilters: filters, setPlanFilters: setFilters } = useAppStore()

  const { orden, articulo, componente, materialSelec, estados, rutasSelec, empresaSelec, tipoArticulo, rotura, roturaDelComponente, pendingStatus, retrasadas, almacenTab, opMin, opMax, sort, sortDir, allExpanded } = filters

  const fmtArt = useFmtArticulo()
  const isAvpOnly = useIsAvpOnly()
  const avp = isAvpOnly

  // Resetear la selección de tipo cuando cambia el modo (Q/P/X ↔ QARF/ARF):
  // los códigos de un modo no son válidos en el otro.
  const prevAvp = useRef(avp)
  useEffect(() => {
    if (prevAvp.current !== avp) {
      prevAvp.current = avp
      setTipoArticulo(new Set())
    }
  }, [avp])

  const setOrden         = (v: string[])            => setFilters({ orden: v })
  const setArticulo      = (v: string[])            => setFilters({ articulo: v })
  const setComponente    = (v: string[])            => setFilters({ componente: v })
  const setEstados       = (v: Set<number>)         => setFilters({ estados: v })
  const setRutasSelec    = (v: Set<string>)         => setFilters({ rutasSelec: v })
  const setEmpresaSelec  = (v: Set<string>)         => setFilters({ empresaSelec: v })
  const setTipoArticulo  = (v: Set<ItemType>)       => setFilters({ tipoArticulo: v })
  const setMaterialSelec = (v: Set<ComponentStatus>) => setFilters({ materialSelec: v })
  const setRotura        = (v: RoturaFilter)        => setFilters({ rotura: v })
  const setRoturaDelComponente = (v: boolean)       => setFilters({ roturaDelComponente: v })
  const setPendingStatus = (v: PendingStatusFilter) => setFilters({ pendingStatus: v })
  const setRetrasadas    = (v: boolean)             => setFilters({ retrasadas: v })
  const setAlmacenTab    = (v: string)              => setFilters({ almacenTab: v })
  const setOpMin         = (v: number)              => setFilters({ opMin: v })
  const setOpMax         = (v: number)              => setFilters({ opMax: v })
  const setSort          = (v: SortKey)             => setFilters({ sort: v })
  const setSortDir       = (v: 'asc'|'desc')        => setFilters({ sortDir: v })
  const setAllExpanded   = (v: boolean)             => setFilters({ allExpanded: v })

  function toggleMaterial(e: ComponentStatus) {
    const s = new Set(materialSelec); s.has(e) ? s.delete(e) : s.add(e); setMaterialSelec(s)
  }
  function toggleEstado(st: number) {
    const s = new Set(estados); s.has(st) ? s.delete(st) : s.add(st); setEstados(s)
  }
  function toggleRuta(r: string) {
    const s = new Set(rutasSelec); s.has(r) ? s.delete(r) : s.add(r); setRutasSelec(s)
  }
  function toggleEmpresa(e: string) {
    const s = new Set(empresaSelec); s.has(e) ? s.delete(e) : s.add(e); setEmpresaSelec(s)
  }
  function toggleTipoArticulo(ty: ItemType) {
    const s = new Set(tipoArticulo); s.has(ty) ? s.delete(ty) : s.add(ty); setTipoArticulo(s)
  }
  function clearAll() {
    setFilters({
      orden: [], articulo: [], componente: [], materialSelec: new Set(),
      estados: new Set(), rutasSelec: new Set(), empresaSelec: new Set(),
      tipoArticulo: new Set(),
      rotura: 'all', roturaDelComponente: false, pendingStatus: 'all', retrasadas: false, almacenTab: '', opMin: 0, opMax: 9999,
    })
  }

  const allRutas = useMemo(
    () => [...new Set(producciones.map(p => p.rutaPrincipal || 'SIN CLASIFICAR'))].sort(),
    [producciones]
  )

  const allAlmacenes = useMemo(
    () => [...new Set(producciones.map(p => p.inventlocationid || '').filter(Boolean))].sort(),
    [producciones]
  )

  // All empresas the user is allowed to see (derived from JWT roles, not from the
  // current dataset). This ensures the filter always appears for multi-empresa users
  // even when the current week/month happens to have orders from only one empresa.
  const allEmpresas  = useAllowedEmpresas()
  // True only for Empresa.All users — gates visibility of Empresa and Almacén filters.
  const isEmpresaAll = useIsEmpresaAll()

  const maxOpAll = useMemo(
    () => producciones.reduce((m, p) => Math.max(m, p.cantidadOperarios), 0),
    [producciones]
  )

  // ── Coincidencia de los buscadores ──────────────────────────────────────────
  // OR entre los términos de un mismo campo, AND entre campos distintos. El
  // código se compara también FORMATEADO (`fmtArt`) porque es el que se ve en
  // pantalla y el que el usuario copia y pega.
  //
  // Los arrays cambian de identidad en cada escritura del store aunque su
  // contenido sea el mismo, así que los memos se cuelgan de la firma y no del
  // array.
  const firmaOrden      = orden.join('|')
  const firmaArticulo   = articulo.join('|')
  const firmaComponente = componente.join('|')

  // Catálogos de valores completos: pedir una orden entera trae esa y no también
  // la que la lleva dentro. Se construyen de lo que hay cargado, que es contra
  // lo que se está filtrando.
  const catOrden = useMemo(
    () => catalogoDe(producciones, p => p.produccion), [producciones])
  const catArticulo = useMemo(() => {
    const c = catalogoDe(producciones, p => p.articulo)
    for (const p of producciones) c.add(fmtArt(p.articulo).trim().toLowerCase())
    return c
  }, [producciones, fmtArt])
  const catComponente = useMemo(() => {
    const c = catalogoDe(planRows, r => r.itemid)
    for (const r of planRows) c.add(fmtArt(r.itemid).trim().toLowerCase())
    return c
  }, [planRows, fmtArt])

  // Catálogos de los dos buscadores que tienen dominio cerrado: se elige de una
  // lista real en vez de teclear a ciegas. Cada opción lleva el código Y su
  // descripción, porque un código suelto no le dice nada a nadie —«ARF12104932»
  // no es «Club de Nuit Woman 105ml»— y la relación es uno a uno.
  //
  // El nº de orden NO lleva catálogo: son cientos de códigos sin nada que los
  // describa, así que una lista no ayudaría a elegir.
  const opcionesArticulo = useMemo(
    () => catalogoArticulos(producciones, p => p.articulo, p => p.nombreArticulo, fmtArt),
    [producciones, fmtArt])
  const opcionesComponente = useMemo(
    () => catalogoArticulos(planRows, r => r.itemid, r => r.nombreMaterial, fmtArt),
    [planRows, fmtArt])

  const casaOrden = useMemo(
    () => predicadoVariasGrafias(orden, catOrden),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firmaOrden, catOrden])

  // El nombre va siempre por subcadena: es una descripción, no un código, y
  // nadie se la sabe entera.
  const casaArticuloProd = useMemo(() => {
    const cod = predicadoVariasGrafias(articulo, catArticulo)
    const nom = predicadoTextoAgregado(articulo)
    return (p: ProduccionCabecera) =>
      cod([p.articulo, fmtArt(p.articulo)]) || nom(p.nombreArticulo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaArticulo, fmtArt, catArticulo])

  /** ¿Casa esta línea de plan con el buscador de componente? */
  const casaComponente = useMemo(() => {
    if (componente.length === 0) return null
    const cod = predicadoVariasGrafias(componente, catComponente)
    const nom = predicadoTextoAgregado(componente)
    return (r: PlanificacionSemanalRow) =>
      cod([r.itemid, fmtArt(r.itemid)]) || nom(r.nombreMaterial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaComponente, fmtArt, catComponente])

  // El semáforo de cada ORDEN, calculado una vez para toda la pantalla con la
  // misma regla que usa Secuencia. Sirve al filtro; la fila lo recalcula de sus
  // propios componentes con la misma función, así que no pueden discrepar.
  const estadoPorProd = useMemo(() => {
    const m = new Map<string, ComponentStatus>()
    for (const [prodid, filas] of planByProd) {
      const estados = filas
        .map(r => asEstadoMaterial(r.estadoMaterial))
        .filter((e): e is ComponentStatus => !!e)
      if (estados.length > 0) m.set(prodid, peorEstado(estados))
    }
    return m
  }, [planByProd])

  const filteredProds = useMemo(() => {
    let list = [...producciones]

    // Empresa multi-select (only when at least one selected)
    if (empresaSelec.size > 0) list = list.filter(p => empresaSelec.has(p.empresa))

    if (almacenTab) list = list.filter(p => p.inventlocationid === almacenTab)

    if (tipoArticulo.size > 0) list = list.filter(p => matchesItemType(p.articulo, tipoArticulo, avp))

    // Los tres se cruzan con AND, que es la corrección: «esta orden» Y «este
    // componente» pide el cruce, no las dos listas pegadas.
    if (orden.length > 0)      list = list.filter(p => casaOrden([p.produccion]))
    if (articulo.length > 0)   list = list.filter(casaArticuloProd)
    if (componente.length > 0 && casaComponente)
      list = list.filter(p => (planByProd.get(p.produccion) ?? []).some(casaComponente))
    if (estados.size > 0)    list = list.filter(p => estados.has(p.status))
    if (rutasSelec.size > 0) list = list.filter(p => rutasSelec.has(p.rutaPrincipal || 'SIN CLASIFICAR'))
    // Una orden sin estado conocido NO pasa el filtro: si se pide ver los
    // rojos, enseñar además los que no sabemos qué son diluye la respuesta.
    if (materialSelec.size > 0) {
      list = list.filter(p => {
        const e = estadoPorProd.get(p.produccion)
        return e ? materialSelec.has(e) : false
      })
    }
    // «Con rotura» tiene dos lecturas y el usuario elige cuál. La de siempre es
    // «esta orden tiene algún componente roto»; la otra es «el componente que
    // estoy buscando es el que rompe», que es lo que se pregunta cuando se
    // persigue un material. El matiz sólo se aplica si hay componente buscado:
    // sin él no hay nada que acotar y filtraría a escondidas.
    const porComponenteRoto = roturaDelComponente && !!casaComponente
    if (rotura === 'con' && porComponenteRoto && casaComponente)
      list = list.filter(p => (planByProd.get(p.produccion) ?? [])
        .some(r => casaComponente(r) && cuentaComoRotura(r)))
    else if (rotura === 'con') list = list.filter(p => prodsConRotura.has(p.produccion))
    if (rotura === 'sin')      list = list.filter(p => !prodsConRotura.has(p.produccion))

    // Filter by unidades_pendientes
    if (pendingStatus === 'pending')  list = list.filter(p => Number(p.unidadesPendientes) > 0)
    if (pendingStatus === 'finished') list = list.filter(p => Number(p.unidadesPendientes) === 0)

    if (retrasadas) {
      const today = new Date(); today.setHours(0, 0, 0, 0)
      list = list.filter(p => {
        const f = parseDate(p.fechaFin)
        return f && f < today && Number(p.unidadesPendientes) > 0
      })
    }

    if (opMin > 0 || opMax < 9999)
      list = list.filter(p => p.cantidadOperarios >= opMin && p.cantidadOperarios <= opMax)

    list.sort((a, b) => {
      let diff = 0
      if (sort === 'fecha') {
        const da = parseDate(a.fechaInicio)?.getTime() ?? 0
        const db = parseDate(b.fechaInicio)?.getTime() ?? 0
        diff = da - db
      } else if (sort === 'unidades') {
        diff = Number(b.unidades) - Number(a.unidades)
      } else {
        const coverage = (p: ProduccionCabecera) => {
          const items = planByProd.get(p.produccion) ?? []
          if (!items.length) return 100
          return Math.min(...items.map(r => r.consumoEstimado > 0 ? Math.min(1, r.disponibleAntes / r.consumoEstimado) : 1)) * 100
        }
        diff = coverage(a) - coverage(b)
      }
      return sortDir === 'asc' ? diff : -diff
    })
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producciones, empresaSelec, tipoArticulo, almacenTab, opMin, opMax,
      firmaOrden, firmaArticulo, firmaComponente, casaOrden, casaArticuloProd, casaComponente,
      materialSelec, estadoPorProd, estados, rutasSelec, rotura, roturaDelComponente, pendingStatus, retrasadas, sort, sortDir, prodsConRotura, planByProd, avp])

  const gruposRuta = useMemo(() => {
    // Only include plan rows whose prodid belongs to a visible (filtered) production
    const filteredIds = new Set(filteredProds.map(p => p.produccion))

    const byRuta  = new Map<string, ProduccionCabecera[]>()
    const pByRuta = new Map<string, PlanificacionSemanalRow[]>()
    filteredProds.forEach(p => {
      const k = p.rutaPrincipal || 'SIN CLASIFICAR'
      const l = byRuta.get(k) ?? []; l.push(p); byRuta.set(k, l)
    })
    planRows
      .filter(r => filteredIds.has(r.prodid))
      .forEach(r => {
        const k = r.rutaPrincipal || 'SIN CLASIFICAR'
        const l = pByRuta.get(k) ?? []; l.push(r); pByRuta.set(k, l)
      })
    return [...byRuta.keys()].sort().map(ruta => ({
      ruta,
      producciones: byRuta.get(ruta)!,
      planRows: pByRuta.get(ruta) ?? [],
    }))
  }, [filteredProds, planRows])

  // ── Despliegue por coincidencias ────────────────────────────────────────────
  // Al buscar, las producciones que han salido POR UN COMPONENTE abren solas su
  // subtabla de consumo y resaltan la línea que las trajo: si no, el usuario ve
  // una OP cuyo código y artículo no se parecen en nada a lo que buscó y no sabe
  // por qué está ahí.
  //
  // El orden es el de PANTALLA (agrupado por ruta) y no el de `filteredProds`,
  // porque las que se abren solas tienen que ser las primeras que el usuario se
  // encuentra al bajar. Las que quedan fuera del tope siguen explicadas por la
  // chapa de coincidencia que pinta cada fila. Ver `opsAutoDesplegadas`.
  const autoAbrirProds = useMemo(() => {
    if (!casaComponente) return new Set<string>()
    const ordenVisible = gruposRuta.flatMap(g => g.producciones.map(p => p.produccion))
    return opsAutoDesplegadas(
      ordenVisible,
      prodid => (planByProd.get(prodid) ?? []).some(casaComponente),
      MAX_AUTO_DESPLIEGUE,
    )
  }, [gruposRuta, casaComponente, planByProd])

  const activeFilters =
    (orden.length ? 1 : 0) +
    (articulo.length ? 1 : 0) +
    (componente.length ? 1 : 0) +
    (materialSelec.size ? 1 : 0) +
    (estados.size ? 1 : 0) +
    (rutasSelec.size ? 1 : 0) +
    (empresaSelec.size ? 1 : 0) +
    (tipoArticulo.size ? 1 : 0) +
    (rotura !== 'all' ? 1 : 0) +
    (pendingStatus !== 'all' ? 1 : 0) +
    (retrasadas ? 1 : 0) +
    (almacenTab ? 1 : 0) +
    (opMin > 0 || opMax < 9999 ? 1 : 0)

  return {
    // filter state
    orden, articulo, componente, materialSelec, estados, rutasSelec, empresaSelec, tipoArticulo, rotura, roturaDelComponente, pendingStatus, retrasadas, almacenTab, opMin, opMax, sort, sortDir, allExpanded,
    // setters
    setOrden, setArticulo, setComponente, setMaterialSelec, setEstados, setRutasSelec, setEmpresaSelec, setTipoArticulo, setRotura, setRoturaDelComponente, setPendingStatus, setRetrasadas, setAlmacenTab,
    setOpMin, setOpMax, setSort, setSortDir, setAllExpanded,
    // helpers
    toggleEstado, toggleMaterial, toggleRuta, toggleEmpresa, toggleTipoArticulo, clearAll,
    // derived
    allRutas, allAlmacenes, allEmpresas, isEmpresaAll, maxOpAll, filteredProds, gruposRuta, activeFilters, avp,
    opcionesArticulo, opcionesComponente,
    estadoPorProd,
    // Despliegue por coincidencias. El «nonce» es la firma de la búsqueda: al
    // cambiar, los componentes que la reciben olvidan los despliegues y colapsos
    // que el usuario hizo a mano con el filtro anterior. Una cadena vale igual
    // que un contador y no obliga a llevar estado extra.
    casaComponente, autoAbrirProds,
    busquedaNonce: `${firmaOrden}~${firmaArticulo}~${firmaComponente}`,
  }
}
